import api from './api';

export type SeatInventorySource = 'EMPTY_LEG' | 'FIXED_ROUTE' | 'ADHOC_RELEASE';
export type SeatInventoryStatus = 'PROPOSED' | 'APPROVAL_PENDING' | 'PUBLISHED' | 'HELD' | 'BOOKED' | 'EXPIRED' | 'CANCELLED';
export type SeatRequestStatus =
  | 'OPEN'
  | 'MATCHED'
  | 'OFFER_SENT'
  | 'ALTERNATIVE_SENT'
  | 'PENDING_APPROVAL'
  | 'DECLINED'
  | 'CLOSED_WON'
  | 'CLOSED_LOST';
export type SeatDecisionOutcome = 'NONE' | 'OFFER_SENT' | 'ALTERNATIVE_SENT' | 'PENDING_APPROVAL' | 'DECLINED';

export interface SeatInventoryItem {
  id: string;
  source: SeatInventorySource;
  status: SeatInventoryStatus;
  opsStatus?: 'OPEN' | 'CLOSED' | 'DEPARTED' | 'CANCELLED';
  mailboxKey?: string | null;
  title?: string | null;
  fromCode: string;
  toCode: string;
  departureAt: string;
  arrivalAt?: string | null;
  totalSeats: number;
  availableSeats: number;
  heldSeats: number;
  seatPriceUSD: number;
  currencyCode: string;
  publishedAt?: string | null;
  metadata?: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeatRequestItem {
  id: string;
  status: SeatRequestStatus;
  lifecycleStatus?: string | null;
  latestOfferStatus?: string | null;
  decisionOutcome: SeatDecisionOutcome;
  category: string;
  inboxMessageId?: string | null;
  emailThreadId?: string | null;
  mailboxKey?: string | null;
  fromEmail?: string | null;
  customerName?: string | null;
  fromCode?: string | null;
  toCode?: string | null;
  travelDate?: string | null;
  preferredTime?: string | null;
  pax?: number | null;
  notes?: string | null;
  assignedToName?: string | null;
  commandOwnerRole?: string | null;
  commandChainLevel: number;
  slaDueAt?: string | null;
  matchedInventory?: {
    id: string;
    fromCode: string;
    toCode: string;
    departureAt: string;
    status: string;
    source: string;
    availableSeats: number;
    seatPriceUSD?: number;
    currencyCode?: string;
    offerId?: string;
    offerStatus?: string;
    lifecycleStatus?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeatPlanBookingDetail {
  id: string;
  status: 'HELD' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED';
  seats: number;
  unitPriceUSD?: number;
  totalPriceUSD?: number;
  currencyCode?: string;
  customerId?: string | null;
  bookingId?: string | null;
  pnrCode?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  quoteId?: string | null;
  notes?: string | null;
  holdUntil?: string | null;
  metadata?: Record<string, any> | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface SeatBookingCreatePayload {
  inventoryId: string;
  requestId?: string;
  offerId?: string;
  quoteId?: string;
  customerId?: string;
  status?: 'HELD' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED';
  seats?: number;
  unitPriceUSD?: number;
  totalPriceUSD?: number;
  currencyCode?: string;
  holdUntil?: string;
  cutoffOverride?: boolean;
  cutoffOverrideReason?: string;
  notes?: string;
  metadata?: Record<string, any>;
}

export interface SeatBookingThreadMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND' | string;
  status?: string;
  subject?: string | null;
  fromEmail?: string | null;
  toEmails?: string | null;
  ccEmails?: string | null;
  bccEmails?: string | null;
  textBody?: string | null;
  htmlBody?: string | null;
  messageId?: string | null;
  inReplyTo?: string | null;
  references?: string | null;
  receivedAt?: string | null;
  sentAt?: string | null;
  createdAt?: string;
}

export interface SeatBookingThreadData {
  thread?: any | null;
  messages: SeatBookingThreadMessage[];
  summary?: {
    sent?: boolean;
    replied?: boolean;
    lastInboundAt?: string | null;
    lastOutboundAt?: string | null;
  };
}

export interface SeatPlanItem {
  id: string;
  source: SeatInventorySource;
  status: SeatInventoryStatus;
  opsStatus?: 'OPEN' | 'CLOSED' | 'DEPARTED' | 'CANCELLED';
  fromCode: string;
  toCode: string;
  departureAt: string;
  arrivalAt?: string | null;
  totalSeats: number;
  availableSeats: number;
  heldSeats: number;
  soldSeats: number;
  seatPriceUSD: number;
  currencyCode: string;
  pricingSource: 'ROUTE_BANK' | 'OVERRIDE' | 'POLICY_DEFAULT' | 'UNKNOWN';
  bookings: SeatPlanBookingDetail[];
}

export interface RouteScheduleSummaryItem {
  routeId?: string | null;
  fromCode: string;
  toCode: string;
  totalSlots: number;
  upcomingSlots: number;
}

export interface SeatBookingPolicy {
  minHoursBeforeDeparture: number;
}

const normalizeList = <T>(res: any): T[] => {
  const data = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(data) ? data : [];
};

export const scheduledSeatService = {
  async listInventory(params?: {
    limit?: number;
    status?: string;
    source?: string;
    fromCode?: string;
    toCode?: string;
  }): Promise<SeatInventoryItem[]> {
    const res = await api.get('/scheduled/inventory', { params });
    return normalizeList<SeatInventoryItem>(res);
  },

  async createInventory(payload: {
    source: SeatInventorySource;
    status?: SeatInventoryStatus;
    fromCode: string;
    toCode: string;
    departureAt: string;
    arrivalAt?: string;
    aircraftId?: string;
    totalSeats?: number;
    availableSeats?: number;
    seatPriceUSD?: number;
    mailboxKey?: string;
    title?: string;
    metadata?: Record<string, any>;
  }): Promise<SeatInventoryItem> {
    const res = await api.post('/scheduled/inventory', payload);
    return (res.data?.data || res.data) as SeatInventoryItem;
  },

  async updateInventory(id: string, payload: {
    departureAt?: string;
    totalSeats?: number;
    availableSeats?: number;
    heldSeats?: number;
    seatPriceUSD?: number;
    title?: string;
    notes?: string;
  }): Promise<SeatInventoryItem> {
    const res = await api.patch(`/scheduled/inventory/${encodeURIComponent(id)}`, payload);
    return (res.data?.data || res.data) as SeatInventoryItem;
  },

  async setInventoryStatus(id: string, status: SeatInventoryStatus): Promise<SeatInventoryItem> {
    const res = await api.patch(`/scheduled/inventory/${encodeURIComponent(id)}/status`, { status });
    return (res.data?.data || res.data) as SeatInventoryItem;
  },

  async setInventoryOpsStatus(
    id: string,
    payload: { opsStatus: 'OPEN' | 'CLOSED' | 'DEPARTED' | 'CANCELLED'; reason?: string }
  ): Promise<SeatInventoryItem> {
    const res = await api.patch(`/scheduled/inventory/${encodeURIComponent(id)}/ops`, payload);
    return (res.data?.data || res.data) as SeatInventoryItem;
  },

  async listRequests(params?: { limit?: number; status?: string; assignedToUserId?: string; myQueue?: boolean }): Promise<SeatRequestItem[]> {
    const res = await api.get('/scheduled/requests', { params });
    return normalizeList<SeatRequestItem>(res);
  },

  async listSeatPlan(params?: {
    limit?: number;
    fromCode?: string;
    toCode?: string;
    departureFrom?: string;
    departureTo?: string;
  }): Promise<SeatPlanItem[]> {
    const res = await api.get('/scheduled/inventory/plan', { params });
    return normalizeList<SeatPlanItem>(res);
  },

  async listRouteSummary(params?: { source?: string }): Promise<RouteScheduleSummaryItem[]> {
    const res = await api.get('/scheduled/inventory/route-summary', { params });
    return normalizeList<RouteScheduleSummaryItem>(res);
  },

  async createRequest(payload: {
    category?: string;
    mailboxKey?: string;
    fromEmail?: string;
    customerName?: string;
    fromCode?: string;
    toCode?: string;
    travelDate?: string;
    preferredTime?: string;
    pax?: number;
    notes?: string;
  }): Promise<SeatRequestItem> {
    const res = await api.post('/scheduled/requests', payload);
    return (res.data?.data || res.data) as SeatRequestItem;
  },

  async setRequestOutcome(id: string, outcome: Exclude<SeatDecisionOutcome, 'NONE'>, note?: string): Promise<SeatRequestItem> {
    const res = await api.patch(`/scheduled/requests/${encodeURIComponent(id)}/outcome`, { outcome, note });
    return (res.data?.data || res.data) as SeatRequestItem;
  },

  async sendRequestOffer(
    id: string,
    payload?: {
      to?: string;
      cc?: string | string[];
      bcc?: string | string[];
      accountId?: string;
      htmlTemplateId?: string;
      pdfTemplateId?: string;
      sendMode?: 'NEW' | 'SAME_THREAD';
      subject?: string;
      html?: string;
      text?: string;
      message?: string;
      offeredSeats?: number;
      offeredPriceUSD?: number;
      currencyCode?: string;
      customerName?: string;
      customerPhone?: string;
      customerDiscountPercent?: number;
      operatorDiscountPercent?: number;
    }
  ): Promise<any> {
    const res = await api.post(`/scheduled/requests/${encodeURIComponent(id)}/send-offer`, payload || {});
    return res.data?.data || res.data;
  },

  async createBooking(payload: SeatBookingCreatePayload): Promise<any> {
    const res = await api.post('/scheduled/bookings', payload);
    return res.data?.data || res.data;
  },

  async updateBookingStatus(
    id: string,
    payload: {
      status: 'HELD' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED';
      holdUntil?: string;
      cutoffOverride?: boolean;
      cutoffOverrideReason?: string;
      notes?: string;
    }
  ): Promise<any> {
    const res = await api.patch(`/scheduled/bookings/${encodeURIComponent(id)}/status`, payload);
    return res.data?.data || res.data;
  },

  async getBookingCancellationPreview(id: string): Promise<any> {
    const res = await api.get(`/scheduled/bookings/${encodeURIComponent(id)}/cancellation-preview`);
    return res.data?.data || res.data;
  },

  async deleteBooking(id: string, payload: { reason: string }): Promise<any> {
    const res = await api.delete(`/scheduled/bookings/${encodeURIComponent(id)}`, { data: payload });
    return res.data?.data || res.data;
  },

  async sendBookingEmail(
    id: string,
    payload?: { to?: string; subject?: string; html?: string; text?: string }
  ): Promise<any> {
    const res = await api.post(`/scheduled/bookings/${encodeURIComponent(id)}/send-email`, payload || {});
    return res.data?.data || res.data;
  },

  async sendBookingWhatsapp(
    id: string,
    payload?: { phoneNumber?: string; message?: string }
  ): Promise<any> {
    const res = await api.post(`/scheduled/bookings/${encodeURIComponent(id)}/send-whatsapp`, payload || {});
    return res.data?.data || res.data;
  },

  async getBookingThread(id: string): Promise<SeatBookingThreadData> {
    const res = await api.get(`/scheduled/bookings/${encodeURIComponent(id)}/thread`);
    return (res.data?.data || { messages: [] }) as SeatBookingThreadData;
  },

  async downloadBookingManifestHtml(id: string, htmlTemplateId?: string): Promise<Blob> {
    const res = await api.get(`/scheduled/bookings/${encodeURIComponent(id)}/manifest/html`, {
      params: htmlTemplateId ? { htmlTemplateId } : undefined,
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  async sendBookingManifestEmail(
    id: string,
    payload?: {
      to?: string;
      cc?: string;
      bcc?: string;
      accountId?: string;
      htmlTemplateId?: string;
      subject?: string;
      html?: string;
      text?: string;
    }
  ): Promise<any> {
    const res = await api.post(`/scheduled/bookings/${encodeURIComponent(id)}/manifest/send-email`, payload || {});
    return res.data?.data || res.data;
  },

  async getBookingPolicy(): Promise<SeatBookingPolicy> {
    const res = await api.get('/scheduled/policy/booking-window');
    return (res.data?.data || res.data || { minHoursBeforeDeparture: 0 }) as SeatBookingPolicy;
  },
};
